import { useCallback, useMemo, useState } from "react";
import { UseEditQuizStateProps } from "./useEditQuizState";
import { ProctoringOption, ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { isEnforcedProfile, isInstituteProfile, isSubAccountProfile } from "@verity/proctoring-option";
import { VerityInstituteSettings } from "../../../types/common";
import { EditQuizRef } from "../../editQuiz";
import { isValidPlatform } from "../../editQuizHelper";
import { PublishLocation } from "../../edit-quiz-modals/SaveProfileModal";

type UseProfileStateProps = Pick<
	UseEditQuizStateProps,
	| "quizType"
	| "api"
	| "enforcedProfilesOnly"
	| "quizInfo"
	| "setCurrentProfileId"
	| "currentProfileId"
	| "setUploadedProfilesMap"
>;

type HandleSaveNewProfileProps = {
	lmsCourseId?: string;
	publishLocation: PublishLocation;
	proctoringOption: Partial<ProctoringOption>;
	profileName: string;
	subAccountIds?: number[];
};

/**Anything related to profiles and enforced profiles */
export default function useProfileState({
	api,
	enforcedProfilesOnly,
	quizInfo,
	quizType,
	setCurrentProfileId,
	currentProfileId,
	setUploadedProfilesMap,
}: UseProfileStateProps) {
	const {
		updateProctoringProfile,
		getProctoringProfiles,
		doesUserHaveInstituteProfilePermissions,
		createProctoringProfile,
		deleteProctoringProfile,
		getPresetProfiles,
	} = api;
	const [profiles, setProfiles] = useState<Array<ProctoringOptionProfileV2>>([]);
	const [hasInstituteProfilePermissions, setHasInstituteProfilePermissions] = useState(false);

	const [isUpdateCurrentProfileClicked, setIsUpdateCurrentProfileClicked] = useState(false); // true when user wants to update the current profile
	const [saveProfileModalShowing, setSaveProfileModalShowing] = useState(false);

	// Whether the user explicitly picked "Create Your Own Configuration". Kept separate from
	// currentProfileId because both "custom selected" and "nothing selected yet" are represented
	// by an undefined id, and ProfileStep remounts on every visit so it needs a signal that
	// survives that to tell the two apart.
	const [isCustomConfig, setIsCustomConfig] = useState(false);

	const getProfile = useCallback(
		(profileId: number) => {
			return profiles.find((p) => p.id === profileId);
		},
		[profiles],
	);

	/**The profile of curruentProfileId */
	const activeProfile = useMemo(() => {
		const foundProfile = currentProfileId ? getProfile(currentProfileId) : undefined;
		if (foundProfile && (!enforcedProfilesOnly || isEnforcedProfile(foundProfile))) {
			return foundProfile;
		} else {
			return undefined;
		}
	}, [currentProfileId, enforcedProfilesOnly, getProfile]);

	const profileMap = useMemo(() => {
		const map = new Map<number, ProctoringOptionProfileV2>(
			profiles.map((x) => [x.id, x] as [number, ProctoringOptionProfileV2]),
		);
		return map;
	}, [profiles]);

	/**Are there any institution, course or user profiles? */
	const hasCreatedProfiles = useMemo(() => {
		return profiles.some(
			(p) => p.type === "institute" || p.type === "course" || p.type === "user" || p.type === "subAccount",
		);
	}, [profiles]);

	/**Technically should never happen. This could happen if all the platforms are disabled */
	const hasPresets = useMemo(() => {
		return profiles.some((p) => p.type === "preset");
	}, [profiles]);

	// ONLY show Profile Step if presets exist or user has created profiles or it is enforced profiles.
	// Profile step would not show if: no presets and no profiles created
	const canShowProfileStep = useMemo(
		() => hasCreatedProfiles || hasPresets || enforcedProfilesOnly,
		[enforcedProfilesOnly, hasCreatedProfiles, hasPresets],
	);

	// Show the preset UI on the profile step if there are presets and the user has never created profiles
	const showPresetGridUI = useMemo(() => {
		return hasPresets && !hasCreatedProfiles;
	}, [hasCreatedProfiles, hasPresets]);

	const fetchAndFilterProfiles = useCallback(
		async (
			verityInstituteSettings: VerityInstituteSettings,
			setupProfileId?: number,
			isSetupGuideWorkflow?: boolean,
		): Promise<Map<number, ProctoringOptionProfileV2>> => {
			const [profilesResponse, presetProfilesResponse] = await Promise.all([
				getProctoringProfiles({ lmsProvidedCourseId: quizInfo.courseId }),
				getPresetProfiles(),
			]);

			const combinedProfiles = [...profilesResponse, ...presetProfilesResponse];

			const filteredProfiles = combinedProfiles.filter(
				(profile) =>
					(!enforcedProfilesOnly || profile.instituteProfile?.enforced) &&
					isValidPlatform({ option: profile.proctoringOption, verityInstituteSettings, quizType }),
			);

			if (isSetupGuideWorkflow) {
				let targetIndex = -1;

				// Find profile passed in
				if (setupProfileId !== undefined && setupProfileId !== null) {
					targetIndex = filteredProfiles.findIndex((p) => p.id === setupProfileId);
				}
				// Find first extension profile
				else {
					targetIndex = filteredProfiles.findIndex((p) => p.proctoringOption.isExtension);
				}

				if (targetIndex > 0) {
					// Get the profile
					const [targetExtension] = filteredProfiles.splice(targetIndex, 1);
					// Put it at the top
					filteredProfiles.unshift(targetExtension);
				}
			}

			const tempProfileMap = new Map(filteredProfiles.map((x) => [x.id, x] as [number, ProctoringOptionProfileV2]));

			// A stored profile can become ineligible when the assessment type or allowed platforms change.
			if (
				currentProfileId !== undefined &&
				combinedProfiles.some((profile) => profile.id === currentProfileId) &&
				!tempProfileMap.has(currentProfileId)
			) {
				setCurrentProfileId(undefined);
				setIsCustomConfig(false);
			}
			setUploadedProfilesMap(tempProfileMap);
			setProfiles(filteredProfiles);

			return tempProfileMap;
		},
		[
			getProctoringProfiles,
			quizInfo.courseId,
			quizType,
			getPresetProfiles,
			setUploadedProfilesMap,
			enforcedProfilesOnly,
			currentProfileId,
			setCurrentProfileId,
		],
	);

	const fetchInstituteProfilePermissions = useCallback(async () => {
		setHasInstituteProfilePermissions(await doesUserHaveInstituteProfilePermissions());
	}, [doesUserHaveInstituteProfilePermissions]);

	const loadProfile = useCallback(
		(profileId: number | undefined) => {
			// Both tests are against undefined rather than truthiness, so an id of 0 is
			// looked up like any other rather than being read as "no id".
			const profile = profileId !== undefined ? getProfile(profileId) : undefined;
			setCurrentProfileId(profileId);
			// Custom means the caller passed no id -- not that the lookup missed. Those differ
			// for an id that resolves to nothing, such as Lumina's synthetic -1, which is a
			// decision already made rather than a request for the custom editor.
			setIsCustomConfig(profileId === undefined);

			return profile;
		},
		[getProfile, setCurrentProfileId],
	);

	const saveNewProfile = useCallback(
		async ({
			lmsCourseId,
			publishLocation,
			proctoringOption,
			profileName,
			subAccountIds,
		}: HandleSaveNewProfileProps) => {
			const isCourseScoped = publishLocation === "course";

			const newProfile = await createProctoringProfile({
				proctoringOption,
				isInstituteProfile: publishLocation === "institute",
				lmsProvidedCourseId: isCourseScoped ? lmsCourseId : undefined,
				name: profileName,
				scope:
					publishLocation === "subAccount" && subAccountIds?.length ? { type: "subAccount", subAccountIds } : undefined,
			});

			setCurrentProfileId(newProfile.id);

			return newProfile;
		},
		[createProctoringProfile, setCurrentProfileId],
	);

	const updateProfile = useCallback(
		async (id: number, proctoringOption: Partial<ProctoringOption>, name: string | undefined) => {
			setIsUpdateCurrentProfileClicked(true);

			return await updateProctoringProfile({
				id,
				proctoringOption,
				name: name?.trim(),
			});
		},
		[updateProctoringProfile],
	);

	const deleteProfile = useCallback(
		async (profile: ProctoringOptionProfileV2) => {
			await deleteProctoringProfile(profile.id);

			if (profile.id === currentProfileId) {
				setCurrentProfileId(undefined);
			}
		},
		[currentProfileId, deleteProctoringProfile, setCurrentProfileId],
	);

	const isDeleteProfileAllowed = useCallback(
		(profile: ProctoringOptionProfileV2) => {
			return (
				((!isInstituteProfile(profile) && !isSubAccountProfile(profile)) || hasInstituteProfilePermissions) &&
				!isEnforcedProfile(profile) &&
				profile.type !== "preset"
			);
		},
		[hasInstituteProfilePermissions],
	);

	const saveAsProfileButtonHandler: EditQuizRef["saveAsProfileButtonHandler"] = useCallback(() => {
		setSaveProfileModalShowing(true);
	}, []);

	const hideSaveProfileModal = useCallback(() => setSaveProfileModalShowing(false), []);

	return {
		state: {
			isUpdateCurrentProfileClicked,
			profiles,
			hasInstituteProfilePermissions,
			profileMap,
			activeProfile,
			isCustomConfig,
			canShowProfileStep,
			showPresetGridUI,
			saveProfileModalShowing,
		},
		action: {
			getProfile,
			updateProfile,
			fetchAndFilterProfiles,
			fetchInstituteProfilePermissions,
			loadProfile,
			saveNewProfile,
			deleteProfile,
			isDeleteProfileAllowed,
			saveAsProfileButtonHandler,
			hideSaveProfileModal,
		},
	};
}
